import { MapPin } from 'lucide-react';

import { Tooltip } from '@/shared/components';

import { cn } from '@/lib/utils';

import { IFormWidgetIconsMap, IFormWidgetProps } from './form-widget.types';

export const formWidgetsIconsMap: IFormWidgetIconsMap = {
	LOCATION: <MapPin className="size-4" />,
	CLOSING_DATE: (
		<svg
			width="16px"
			height="16px"
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M21 10H3M16 2V6M8 2V6M7.8 22H16.2C17.8802 22 18.7202 22 19.362 21.673C19.9265 21.3854 20.3854 20.9265 20.673 20.362C21 19.7202 21 18.8802 21 17.2V8.8C21 7.11984 21 6.27976 20.673 5.63803C20.3854 5.07354 19.9265 4.6146 19.362 4.32698C18.7202 4 17.8802 4 16.2 4H7.8C6.11984 4 5.27976 4 4.63803 4.32698C4.07354 4.6146 3.6146 5.07354 3.32698 5.63803C3 6.27976 3 7.11984 3 8.8V17.2C3 18.8802 3 19.7202 3.32698 20.362C3.6146 20.9265 4.07354 21.3854 4.63803 21.673C5.27976 22 6.11984 22 7.8 22Z"
				stroke="currentColor"
				strokeWidth="2.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	),
	NUMBER_OF_APPLICANTS: (
		<svg
			width="16px"
			height="16px"
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M22 7L14.1314 14.8686C13.7354 15.2646 13.5373 15.4627 13.309 15.5368C13.1082 15.6021 12.8918 15.6021 12.691 15.5368C12.4627 15.4627 12.2646 15.2646 11.8686 14.8686L9.13137 12.1314C8.73535 11.7354 8.53735 11.5373 8.30902 11.4632C8.10817 11.3979 7.89183 11.3979 7.69098 11.4632C7.46265 11.5373 7.26465 11.7354 6.86863 12.1314L2 17M22 7H15M22 7V14"
				stroke="currentColor"
				strokeWidth="2.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	),
	RESPONSE_PERCENTAGE: (
		<svg
			width="100%"
			height="100%"
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M13 2L4.09344 12.6879C3.74463 13.1064 3.57023 13.3157 3.56756 13.4925C3.56524 13.6461 3.63372 13.7923 3.75324 13.8889C3.89073 14 4.16316 14 4.70802 14H12L11 22L19.9065 11.3121C20.2553 10.8936 20.4297 10.6843 20.4324 10.5075C20.4347 10.3539 20.3663 10.2077 20.2467 10.1111C20.1092 10 19.8368 10 19.292 10H12L13 2Z"
				stroke="currentColor"
				strokeWidth="2.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	),
};

const getColorClasses = (color: string) => {
	switch (color) {
		case 'blue':
			return {
				bg: 'bg-blue-50',
				border: 'border-blue-100',
				text: 'text-blue-800',
			};
		case 'pink':
			return {
				bg: 'bg-pink-50',
				border: 'border-pink-100',
				text: 'text-pink-800',
			};
		case 'green':
			return {
				bg: 'bg-green-50',
				border: 'border-green-100',
				text: 'text-green-800',
			};
		case 'orange':
			return {
				bg: 'bg-orange-50',
				border: 'border-orange-100',
				text: 'text-orange-800',
			};
		case 'purple':
			return {
				bg: 'bg-purple-50',
				border: 'border-purple-100',
				text: 'text-purple-800',
			};
		case 'gray':
			return {
				bg: 'bg-gray-50',
				border: 'border-gray-100',
				text: 'text-gray-800',
			};
		case 'red':
			return {
				bg: 'bg-red-50',
				border: 'border-red-100',
				text: 'text-red-800',
			};
		default:
			return {
				bg: 'bg-blue-50',
				border: 'border-blue-100',
				text: 'text-blue-800',
			};
	}
};

function FormWidget({
	text,
	color = 'blue',
	widgetType,
	tooltipText,
	className,
	variant = 'default',
	wrapText = false,
}: IFormWidgetProps) {
	// Get color classes based on the color prop
	const colorClasses = getColorClasses(color);

	const icon = formWidgetsIconsMap[widgetType as keyof IFormWidgetIconsMap];

	const isMinimal = variant === 'minimal';

	return (
		<Tooltip content={tooltipText}>
			<div
				className={cn(
					'flex w-fit max-w-full overflow-hidden transition-colors',
					wrapText ? 'items-start' : 'items-center',
					isMinimal
						? 'gap-1.5 rounded-sm px-1 py-0.5 text-sm font-medium'
						: 'gap-2 rounded-md border p-2 px-4 text-sm font-semibold leading-6',
					!isMinimal && colorClasses.border,
					!isMinimal && colorClasses.bg,
					colorClasses.text,
					className
				)}
			>
				{/* One line tall, so the icon stays centred on the first line when the text wraps. */}
				<span className="flex h-[1lh] flex-shrink-0 items-center">
					<span className={cn('inline-block', isMinimal ? 'h-3.5 w-3.5' : 'h-4 w-4')}>{icon}</span>
				</span>
				<p className={wrapText ? 'min-w-0 break-words' : 'truncate'}>{text}</p>
			</div>
		</Tooltip>
	);
}
export default FormWidget;
